import { useEffect, useRef, useState } from 'react';
import {
  PaymentMethodState,
  useGetVehicleByIdV2Query,
} from '../../../../../../codegen/vehicles';
import { usePaymentMethodUpdateState } from '../../../../../../state/vehicles/paymentMethodUpdateSlice';
import { PaymentMethod } from '../../../../../../models';
import { appConfiguration } from '../../../../../../configuration/appConfiguration';

const POLLING_INTERVAL_MS =
  appConfiguration.paymentMethodStatusUpdatePolling.intervalMs;
const MAX_POLL_ATTEMPTS =
  appConfiguration.paymentMethodStatusUpdatePolling.maxAttempts;

/**
 * Polls requests to detect payment method state transitions
 * from ONBOARDING_PENDING -> ACTIVE or ONBOARDING_PENDING -> ONBOARDING_FAILURE.
 *
 * Polling starts when there are pending payment methods and stops when:
 * - All pending methods have resolved, or
 * - MAX_POLL_ATTEMPTS is reached, or
 *
 * When a new pending payment method appears (e.g. user adds another card),
 * the attempt counter resets automatically.
 */
export const usePaymentMethodPolling = (
  vehicleId = '',
  paymentMethods: PaymentMethod[] = [],
) => {
  const { setPaymentMethodUpdate } = usePaymentMethodUpdateState();

  const pollCount = useRef(0);
  const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);

  const pendingIds = paymentMethods
    .filter((pm) => pm.state === PaymentMethodState.OnboardingPending)
    .map((pm) => pm.id)
    .join(',');

  // Reset counter when the set of pending payment methods changes (e.g. new card added)
  useEffect(() => {
    pollCount.current = 0;
    setMaxAttemptsReached(false);
  }, [pendingIds]);

  const shouldPoll = !!pendingIds && !maxAttemptsReached;

  const { data: polledVehicle, fulfilledTimeStamp } = useGetVehicleByIdV2Query(
    { vehicleId },
    {
      skip: !shouldPoll,
      pollingInterval: POLLING_INTERVAL_MS,
    },
  );

  useEffect(() => {
    if (!polledVehicle || !shouldPoll || !fulfilledTimeStamp) return;

    pollCount.current += 1;
    if (pollCount.current >= MAX_POLL_ATTEMPTS) {
      setMaxAttemptsReached(true);
    }

    for (const vPm of polledVehicle.paymentMethods) {
      const currentPm = paymentMethods.find((pm) => pm.id === vPm.id);
      if (
        currentPm?.state === PaymentMethodState.OnboardingPending &&
        vPm.state !== PaymentMethodState.OnboardingPending
      ) {
        setPaymentMethodUpdate({
          vehicleId,
          paymentMethodId: vPm.id,
          paymentMethodState: vPm.state,
        });
      }
    }
  }, [fulfilledTimeStamp]);
};
